import { jsx as _jsx } from "react/jsx-runtime";
/**
 * `SpriteBase3D::_get_color_accum()` (`sprite_3d.cpp:36-52`): a sprite's drawn
 * colour is its parent's accumulated colour × its own modulate, all four channels,
 * used as the vertex colour at `:124`. `parent_sprite` is the immediate parent only
 * (`sprite_3d.cpp:75`), so this is not the cascading `Modulate2DContext` shape.
 */
import { createContext, useContext, useMemo } from 'react';
import { multiplyModulate, WHITE_MODULATE } from './canvasItemModulate.js';
/** The `SpriteBase3D` subclasses (`sprite_3d.h:36`): `cast_to<SpriteBase3D>` in type-name terms. */
const SPRITE_BASE_3D_TYPES = new Set(['Sprite3D']);
/** The immediate parent's accumulation, white unless that parent is a SpriteBase3D. */
const ParentColorAccumContext = createContext(WHITE_MODULATE);
/** One node's step of the accumulation: the four `*=` at `sprite_3d.cpp:47-50`. */
function accumulate(parentAccum, modulate) {
    return multiplyModulate(parentAccum, modulate);
}
/** This sprite's own `_get_color_accum()`, in the sRGB space Godot multiplies in. */
export function useSpriteBase3DColorAccum(modulate) {
    const parentAccum = useContext(ParentColorAccumContext);
    return useMemo(() => accumulate(parentAccum, modulate), [parentAccum, modulate]);
}
/**
 * Publishes `node`'s accumulation to its children, or white outside the family.
 * It takes a node, never a value, and the dispatcher wraps every node's children
 * in it, so an intervening non-sprite restarts the accumulation at white.
 */
export function SpriteBase3DChildAccum({ node, children }) {
    const parentAccum = useContext(ParentColorAccumContext);
    const modulate = node.properties.modulate;
    const value = useMemo(() => SPRITE_BASE_3D_TYPES.has(node.type) && modulate ? accumulate(parentAccum, modulate) : WHITE_MODULATE, [node.type, modulate, parentAccum]);
    return _jsx(ParentColorAccumContext.Provider, { value: value, children: children });
}
//# sourceMappingURL=spriteBase3DColorAccum.js.map